import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import debounce from 'lodash/debounce';
import store from '@/store';
import { config } from '@/config.js';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';
import { useToast } from '@/composables/common/useToast';
import { useQueryList as useListQuery } from '@/composables/common/useQueryList';

export function useBasesIndex() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const vm = getCurrentInstance();

    // Fuente única de verdad para el lista_id de ESTA instancia.
    // `list.listaId` (de useQueryList) captura un snapshot de la ruta al montar
    // y queda stale al cambiar de temporada. Derivándolo de vm.proxy.$route
    // (reactiva de verdad) garantizamos que listaId, fetch y las operaciones CRUD
    // usen SIEMPRE el id actual.
    const currentListaId = computed(() => vm.proxy.$route.params.lista_id);

    const list = useListQuery({
        fetcher: async params => {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.index'),
                params
            );
            return res.data || { data: [], meta: {} };
        },
        extraFilters: ['search_for', 'categoria', 'metodo_pago', 'equipo'],
        defaultFilterValues: { search_for: 'nombre' },
        autoFetch: false,
        // El refetch por cambio de ruta se gestiona aquí (watcher de abajo) pasando
        // el newId; desactivamos el interno para evitar dobles fetchs.
        watchListaId: false,
    });

    const categorias = ref([]);
    const equipos = ref([]);

    const urlDescargaInforme = computed(() =>
        config.API_BASE + '/bases/informe?api_key=' + localStorage.getItem('token')
    );

    // ── Wrappers que siempre pasan el lista_id actual ──────────────────────

    function fetch(lista_id) {
        return list.fetch(lista_id ?? currentListaId.value);
    }
    const debouncedFetch = debounce(id => fetch(id), 500);

    // ── Secondary data ──────────────────────────────────────────────────────

    async function getCategorias(lista_id) {
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.get.categorias'),
                { lista_id: lista_id || currentListaId.value, filters: {} }
            );
            categorias.value = res.data.data || [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching categorias:', error);
        }
    }

    async function getEquipos(lista_id) {
        try {
            // equiposAll devuelve equipos de TODAS las listas/temporadas del teatro,
            // cuyos ids no coinciden con los equipo_id de las bases de esta lista.
            // equipos.index aplica el scope porLista y sus ids sí matchean el filtro.
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.equipos.index'),
                { lista_id: lista_id || currentListaId.value, filters: {} }
            );
            equipos.value = res.data.data || [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching equipos:', error);
        }
    }

    // ── CRUD ────────────────────────────────────────────────────────────────

    async function borrar(baseId) {
        const params = {
            lista_id: currentListaId.value,
            base_id: baseId,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.borrar'),
                params
            );
            toast.success(res.data.message);
            fetch();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al borrar el deportista');
        }
    }

    // ── Lifecycle ───────────────────────────────────────────────────────────

    onMounted(() => {
        fetch();
        getCategorias();
        getEquipos();
    });

    // Re-fetch secondary data when route lista_id changes.
    // IMPORTANTE: usamos vm.proxy.$route en vez de la variable `route` capturada.
    // useRoute() en este proyecto devuelve un snapshot del $route en el momento
    // de la llamada, no una referencia reactiva. Accediendo via vm.proxy.$route
    // en el watch source, Vue siempre evalúa el valor ACTUAL del route.
    watch(() => vm.proxy.$route.params.lista_id, (newId, oldId) => {
        if (newId && newId !== oldId) {
            fetch(newId);
            getCategorias(newId);
            getEquipos(newId);
        }
    });

    onUnmounted(() => {
        debouncedFetch.cancel();
    });

    // ── Return ──────────────────────────────────────────────────────────────

    return {
        bases: list.data,
        loading: list.loading,
        filters: list.filters,
        categorias,
        equipos,
        listaId: currentListaId,
        urlDescargaInforme,
        fetch,
        getCategorias,
        getEquipos,
        borrar,
        debouncedFetch,
        updateUrlQuery: list.updateUrlQuery,
        isHydrating: list.isHydrating,
    };
}
